// getIsMatchedPathname.js
'use strict';
import { matchPath } from 'react-router';
import { freezoneType } from '../resource/freezoneConstants.js';
import { LIVESTREAM_PATH } from '../resource/liveStreamConstants.js';
import { WHITE_LIST_PATHS_REGEX } from '../resource/nextjsWhiteListPaths.js';

const matchOptions = {
  isOnNext: {
    paths: WHITE_LIST_PATHS_REGEX.map(path => ({
      path,
      ends: true,
    })),
  },
  isOnFreezone: {
    amatuer: {
      end: true,
      path: `/freezone/${freezoneType.AMATEUR}`,
    },
    model: {
      end: true,
      path: `/freezone/${freezoneType.MODEL}`,
    },
  },
  isOnFreezoneDetail: {
    end: true,
    path: '/freezone/:id',
  },
  isOnFlixDetail: {
    end: true,
    path: '/flix/:id',
  },
  isOnJoin: {
    end: true,
    path: '/join/:progress',
  },
  isOnPostDetail: {
    paths: [
      {
        path: '/post/:id',
        end: true,
      },
      {
        path: '/chat/:chatroomId/message/:messageId',
        end: true,
      },
    ],
  },
  isOnChatUser: {
    end: true,
    path: '/chat/user/:id',
  },
  isOnProfile: {
    paths: [
      {
        end: true,
        path: '/u/:username',
      },
      {
        end: true,
        path: '/u/:username/:listType',
      },
      {
        end: true,
        path: '/user/:userId',
      },
      {
        end: true,
        path: '/user/:userId/:listType',
      },
    ],
  },
  isOnLivestreamDetail: {
    paths: [
      {
        end: true,
        path: `/u/:username/${LIVESTREAM_PATH}`,
      },
      {
        end: true,
        path: `/user/:userId/${LIVESTREAM_PATH}`,
      },
    ],
  },
  isOnLivestreamChat: {
    paths: [
      {
        end: true,
        path: `/u/:username/${LIVESTREAM_PATH}/chat`,
      },
      {
        end: true,
        path: `/user/:userId/${LIVESTREAM_PATH}/chat`,
      },
    ],
  },
  isOnFaq: {
    end: true,
    path: '/settings/faq',
  },
  isOnTos: {
    end: true,
    path: '/tos',
  },
  isOnPrivacy: {
    end: true,
    path: '/privacy',
  },
  isOnDebug: {
    end: true,
    path: '/settings/more/debug',
  },
  isOnAbout: {
    end: true,
    path: '/settings/more/about',
  },
  isOn2257: {
    end: true,
    path: '/settings/2257',
  },
  isOnAddvalue: {
    end: true,
    path: '/addvalue',
  },
  isOnRevenue: {
    paths: [
      {
        end: true,
        path: '/revenue',
      },
      {
        end: true,
        path: '/revenue/report/:reportType',
      },
    ],
  },
};

/**
 * Get is matched pathname
 * @param {string} pathname - current pathname.
 * @param {string} dataKey - data key.
 * @param {object} {option} - `matchPath` options.
 * @returns {boolean} return true if current pathname is on certain pathname.
 */
const getIsMatchedPathname = ({ pathname, dataKey, option = {} } = {}) => {
  if (!pathname || !dataKey || !matchOptions[dataKey]) {
    return false;
  }
  switch (dataKey) {
    case 'isOnFreezoneDetail': {
      const isOnFreezone = getIsOnFreezone({ pathname }) !== null;
      const isOnFreezoneDetail =
        matchPath(matchOptions[dataKey], pathname) != null;
      return !isOnFreezone && isOnFreezoneDetail;
    }
    case 'isOnFreezone': {
      return getIsOnFreezone({ pathname }) !== null;
    }
    case 'isOnProfile': {
      const profilePatterns = matchOptions[dataKey]?.paths || [];
      const isOnLivestream = [
        ...(matchOptions['isOnLivestreamDetail']?.paths || []),
        ...(matchOptions['isOnLivestreamChat']?.paths || []),
      ].some(pattern => matchPath(pattern, pathname) != null);
      const isOnProfile = profilePatterns.some(
        pattern => matchPath(pattern, pathname) != null
      );
      return isOnProfile && !isOnLivestream;
    }
    case 'isOnNext': {
      return matchOptions[dataKey].paths.some(
        pattern => matchPath({ ...pattern, ...option }, pathname) != null
      );
    }
    default: {
      if (!matchOptions[dataKey]) return false;
      const patterns = [];
      const paths = matchOptions[dataKey].paths;
      if (paths) {
        patterns.push(...paths);
      } else {
        patterns.push(matchOptions[dataKey]);
      }
      return patterns.some(pattern => matchPath(pattern, pathname) != null);
    }
  }
};

export default getIsMatchedPathname;

const getIsOnFreezone = ({ pathname }) => {
  if (!pathname) return;

  const amateuer = matchPath(matchOptions.isOnFreezone.amatuer, pathname);
  const model = matchPath(matchOptions.isOnFreezone.model, pathname);
  return amateuer || model;
};
